import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface SelectOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
}

interface SelectInputProps {
  label?: string;
  placeholder?: string;
  id?: string;
  isRequired?: boolean;
  value?: string;
  onChange?: (value: string) => void;
  error?: string;
  name?: string;
  options?: SelectOption[];
  portal?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

export default function SelectInput({
  label,
  placeholder = 'Select an option',
  id,
  isRequired,
  value,
  onChange,
  error,
  name,
  options = [],
  portal = false,
  size = 'md',
}: SelectInputProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  const sizeMap = {
    sm: {
      trigger: 'py-[6px]',
      option: 'py-[6px]',
      text: 'text-xs',
      icon: 'w-3 h-3',
    },
    md: {
      trigger: 'py-[10px]',
      option: 'py-[10px]',
      text: 'text-sm md:text-base lg:text-sm 3xl:text-base',
      icon: 'w-4 h-4',
    },
    lg: {
      trigger: 'py-[14px]',
      option: 'py-[14px]',
      text: 'text-base md:text-lg lg:text-sm 3xl:text-sm',
      icon: 'w-5 h-5',
    },
    xl: {
      trigger: 'py-[18px]',
      option: 'py-[18px]',
      text: 'text-lg md:text-xl lg:text-lg 3xl:text-xl',
      icon: 'w-6 h-6',
    },
  };

  const currentSize = sizeMap[size] || sizeMap.md;

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && portal && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  }, [isOpen, portal]);

  const handleSelect = (option: any) => {
    onChange?.(option);
    setIsOpen(false);
  };

  const selectedOption = options.find((opt) => opt.value === value);

  const dropdownContent = isOpen && (
    <div
      ref={dropdownRef}
      className="absolute z-[1000] mt-1 bg-modal-input border-0.5 border-gray-3/20 rounded-md shadow-lg max-h-60 overflow-auto w-full"
      style={
        portal
          ? {
              position: 'fixed',
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              width: `${dropdownPosition.width}px`,
              marginTop: 0,
            }
          : undefined
      }
    >
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => handleSelect(option.value)}
          className={`w-full text-left px-[14px] ${currentSize.option} ${currentSize.text} hover:pl-5 hover:bg-gray-3/10 transition-all duration-300 ease-in-out flex items-center gap-2
            ${value === option.value ? 'bg-gray-3/20 text-text-primary-highlight font-medium' : 'text-text-primary-highlight'}`}
        >
          {option.icon && <span className="flex-shrink-0">{option.icon}</span>}
          {option.label}
        </button>
      ))}
    </div>
  );

  return (
    <div className="w-full flex flex-col items-start gap-1">
      <label
        htmlFor={id || name}
        className="text-left text-xs md:text-sm lg:text-sm 3xl:text-base text-input-active-label font-semibold"
      >
        {label} {isRequired && <span className="text-sm text-error-1">*</span>}
      </label>

      <div
        className={portal ? 'w-full' : 'relative w-full'}
        ref={portal ? undefined : dropdownRef}
      >
        <div
          ref={triggerRef}
          onClick={() => setIsOpen(!isOpen)}
          className={`cursor-pointer placeholder:text-input-inactive-placeholder w-full rounded-md ${currentSize.trigger} px-[14px] focus:outline-none ${currentSize.text} text-text-primary-highlight transition-all bg-modal-input text-left flex items-center justify-between
            ${error ? 'border-0.5 border-error-2' : 'border-0.5 border-gray-3/20'}`}
        >
          <span
            className={`flex items-center gap-2 ${
              selectedOption
                ? 'text-text-primary-highlight'
                : 'text-input-inactive-placeholder text-sm 3xl:text-base'
            }`}
          >
            {selectedOption?.icon && (
              <span className="flex-shrink-0">{selectedOption.icon}</span>
            )}
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <svg
            className={`${currentSize.icon} transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>

        {portal
          ? createPortal(dropdownContent, document.body)
          : dropdownContent}
      </div>

      {error && <p className="text-error-2 text-xs md:text-xs mt-1">{error}</p>}
    </div>
  );
}
